import { supabase, SCREENSHOTS_BUCKET, SIGNED_URL_TTL } from './supabaseClient'
import { DayJournal, JournalBlock, JournalState, Operation, OperationImage, Tag, TagCategory } from './types'
import { uid } from './id'

/** Every new account starts with its own editable copy of the seed tags. */
function seedTags(): { categories: TagCategory[]; tags: Tag[] } {
  const direccion = uid('cat')
  const zona = uid('cat')
  const entrada = uid('cat')

  const categories: TagCategory[] = [
    { id: direccion, name: 'Dirección', timeframe: 'Diario', multiple: false, order: 0 },
    { id: zona, name: 'Zona', timeframe: 'H1', multiple: false, order: 1 },
    { id: entrada, name: 'Entrada', timeframe: 'M5', multiple: false, order: 2 }
  ]

  const spec: [string, string[]][] = [
    [direccion, ['Vela atrapada', 'Continuación', 'Rango']],
    [zona, ['Liquidez', 'Ineficiencia']],
    [entrada, ['OB M5 + OB M1', 'OB H1 + OB M5', 'OB M5', 'Reentrada INF M5']]
  ]

  const tags: Tag[] = []
  spec.forEach(([categoryId, labels]) => {
    labels.forEach((label, order) => {
      tags.push({ id: uid('tag'), categoryId, label, color: '#8b9099', order })
    })
  })

  return { categories, tags }
}

function createEmptyState(userId: string): JournalState {
  const { categories, tags } = seedTags()
  return { userId, operations: [], categories, tags, dayJournals: {} }
}

function toIso(ms?: number | null): string | null {
  return typeof ms === 'number' ? new Date(ms).toISOString() : null
}

function fromIso(iso?: string | null): number | undefined {
  return iso ? new Date(iso).getTime() : undefined
}

async function signedUrlFor(path: string): Promise<string> {
  const { data } = await supabase.storage.from(SCREENSHOTS_BUCKET).createSignedUrl(path, SIGNED_URL_TTL)
  return data?.signedUrl ?? ''
}

export async function loadState(userId: string): Promise<JournalState> {
  if (!userId) return createEmptyState('')

  try {
    const [{ data: cats }, { data: tagsData }, { data: ops }, { data: journals }] = await Promise.all([
      supabase.from('tag_categories').select('*').eq('user_id', userId).order('order_index'),
      supabase.from('tags').select('*').eq('user_id', userId).order('order_index'),
      supabase.from('operations').select('*, operation_images(*)').eq('user_id', userId),
      supabase.from('day_journals').select('*, journal_blocks(*)').eq('user_id', userId)
    ])

    let categories: TagCategory[] = (cats ?? []).map((c: any) => ({
      id: c.id,
      name: c.name,
      timeframe: c.timeframe ?? undefined,
      multiple: c.multiple,
      order: c.order_index
    }))

    let tagList: Tag[] = (tagsData ?? []).map((t: any) => ({
      id: t.id,
      categoryId: t.category_id,
      label: t.label,
      color: t.color ?? '#8b9099',
      order: t.order_index,
      archived: t.archived
    }))

    // A brand new account receives its own editable copy of the seed tags.
    if (!categories.length) {
      const seeded = seedTags()
      categories = seeded.categories
      tagList = seeded.tags
      await supabase.from('tag_categories').insert(
        categories.map((c) => ({
          id: c.id,
          user_id: userId,
          name: c.name,
          timeframe: c.timeframe ?? null,
          multiple: c.multiple,
          order_index: c.order
        }))
      )
      await supabase.from('tags').insert(
        tagList.map((t) => ({
          id: t.id,
          user_id: userId,
          category_id: t.categoryId,
          label: t.label,
          color: t.color,
          order_index: t.order,
          archived: false
        }))
      )
    }

    const operations: Operation[] = await Promise.all(
      (ops ?? []).map(async (op: any) => {
        const images: OperationImage[] = await Promise.all(
          (op.operation_images ?? []).map(async (img: any) => ({
            id: img.id,
            name: img.name ?? '',
            url: await signedUrlFor(img.storage_path)
          }))
        )

        return {
          id: op.id,
          userId: op.user_id,
          date: op.date,
          entryTime: op.entry_time ?? '',
          exitTime: op.exit_time ?? '',
          asset: op.asset,
          side: op.side,
          resultPct: Number(op.result_pct ?? 0),
          resultMoney: op.result_money != null ? Number(op.result_money) : undefined,
          resultR: Number(op.result_r ?? 0),
          followedPlan: op.followed_plan,
          notes: op.notes ?? '',
          images,
          tagIds: op.tag_ids ?? [],
          createdAt: fromIso(op.created_at) ?? Date.now(),
          deletedAt: fromIso(op.deleted_at) ?? null,
          permanentDeleteAt: fromIso(op.permanent_delete_at) ?? null,
          trashedWithDay: op.trashed_with_day ?? null
        } as Operation
      })
    )

    const dayJournals: Record<string, DayJournal> = {}
    for (const j of journals ?? []) {
      const blocks: JournalBlock[] = (j.journal_blocks ?? [])
        .map((b: any) => ({
          id: b.id,
          type: b.type,
          content: b.content ?? '',
          caption: b.caption ?? undefined,
          checked: b.checked ?? undefined,
          width: b.width ?? undefined,
          order: b.order_index
        }))
        .sort((a: JournalBlock, b: JournalBlock) => a.order - b.order)

      dayJournals[j.date] = {
        userId: j.user_id,
        date: j.date,
        title: j.title ?? '',
        followedPlan: j.followed_plan,
        reviewStatus: j.review_status ?? 'none',
        blocks,
        updatedAt: fromIso(j.updated_at) ?? Date.now(),
        deletedAt: fromIso(j.deleted_at) ?? null,
        permanentDeleteAt: fromIso(j.permanent_delete_at) ?? null
      }
    }

    return { userId, operations, categories, tags: tagList, dayJournals }
  } catch {
    return createEmptyState(userId)
  }
}

/**
 * Thrown by saveState when any sync step fails. Keeps the original Supabase
 * error in `cause` and the step that failed in `step`, so callers can log the
 * real reason instead of losing it.
 */
export class SaveError extends Error {
  readonly step: string
  readonly reason: unknown

  constructor(message: string, step: string, reason?: unknown) {
    super(message)
    this.name = 'SaveError'
    this.step = step
    this.reason = reason
  }
}

/** Human-readable Spanish message for the errors Supabase can return on write. */
function describeSaveError(error: unknown): string {
  const raw =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message ?? '')
      : ''
  const message = raw.toLowerCase()

  if (message.includes('row-level security') || message.includes('violates row-level') || message.includes('permission denied')) {
    return 'Tu sesión no tiene permiso para guardar estos datos. Vuelve a iniciar sesión e inténtalo de nuevo.'
  }
  if (message.includes('jwt') || message.includes('token') || message.includes('not authenticated')) {
    return 'La sesión ha caducado. Vuelve a iniciar sesión para guardar los cambios.'
  }
  if (message.includes('failed to fetch') || message.includes('networkerror') || message.includes('network')) {
    return 'Sin conexión con el servidor. Los cambios no se han guardado todavía.'
  }
  return raw ? `No se han podido guardar los cambios: ${raw}` : 'No se han podido guardar los cambios.'
}

/** Runs one sync step and rethrows its error as a SaveError, never swallowing it. */
async function runStep(step: string, run: () => Promise<void>): Promise<void> {
  try {
    await run()
  } catch (error) {
    if (typeof console !== 'undefined') {
      console.error(`[DZE Journal] Error al guardar (${step}):`, error)
    }
    throw new SaveError(describeSaveError(error), step, error)
  }
}

/**
 * Persists the user's dataset. Every step (categories, tags, operations, day
 * journals) rethrows its error as a SaveError — including an RLS rejection — so
 * the UI can never claim "Guardado" when the write actually failed.
 *
 * @throws {SaveError} when there is no active session or any sync step fails.
 */
export async function saveState(userId: string, state: JournalState): Promise<void> {
  if (!userId) {
    throw new SaveError('No hay sesión activa, los cambios no se han guardado.', 'sesión')
  }

  await runStep('categorías', () => syncCategories(userId, state.categories))
  await runStep('etiquetas', () => syncTags(userId, state.tags))
  await runStep('operaciones', () => syncOperations(userId, state.operations))
  await runStep('journals diarios', () => syncDayJournals(userId, state.dayJournals))
}

/** Throws when Supabase rejects a write, so saveState can report the failure. */
function assertOk(result: { error: unknown }, context: string): void {
  if (result?.error) {
    const message =
      result.error && typeof result.error === 'object' && 'message' in result.error
        ? String((result.error as { message?: unknown }).message ?? '')
        : String(result.error)
    throw new Error(`${context}: ${message}`)
  }
}

async function syncCategories(userId: string, categories: TagCategory[]): Promise<void> {
  const { data: existing } = await supabase.from('tag_categories').select('id').eq('user_id', userId)
  const existingIds = new Set((existing ?? []).map((r: any) => r.id))
  const currentIds = new Set(categories.map((c) => c.id))
  const toDelete = [...existingIds].filter((id) => !currentIds.has(id))

  if (toDelete.length) {
    assertOk(await supabase.from('tag_categories').delete().in('id', toDelete), 'categorías (borrado)')
  }

  if (categories.length) {
    assertOk(
      await supabase.from('tag_categories').upsert(
        categories.map((c) => ({
          id: c.id,
          user_id: userId,
          name: c.name,
          timeframe: c.timeframe ?? null,
          multiple: c.multiple,
          order_index: c.order
        }))
      ),
      'categorías'
    )
  }
}

async function syncTags(userId: string, tags: Tag[]): Promise<void> {
  const { data: existing } = await supabase.from('tags').select('id').eq('user_id', userId)
  const existingIds = new Set((existing ?? []).map((r: any) => r.id))
  const currentIds = new Set(tags.map((t) => t.id))
  const toDelete = [...existingIds].filter((id) => !currentIds.has(id))

  if (toDelete.length) {
    assertOk(await supabase.from('tags').delete().in('id', toDelete), 'etiquetas (borrado)')
  }

  if (tags.length) {
    assertOk(
      await supabase.from('tags').upsert(
        tags.map((t) => ({
          id: t.id,
          user_id: userId,
          category_id: t.categoryId,
          label: t.label,
          color: t.color ?? null,
          order_index: t.order,
          archived: !!t.archived
        }))
      ),
      'etiquetas'
    )
  }
}

async function syncOperations(userId: string, operations: Operation[]): Promise<void> {
  const { data: existing } = await supabase.from('operations').select('id').eq('user_id', userId)
  const existingIds = new Set((existing ?? []).map((r: any) => r.id))
  const currentIds = new Set(operations.map((o) => o.id))
  const toDelete = [...existingIds].filter((id) => !currentIds.has(id))

  if (toDelete.length) {
    for (const id of toDelete) await deleteOperationImages(id)
    assertOk(await supabase.from('operations').delete().in('id', toDelete), 'operaciones (borrado)')
  }

  if (operations.length) {
    assertOk(
      await supabase.from('operations').upsert(
        operations.map((o) => ({
          id: o.id,
          user_id: userId,
          date: o.date,
          entry_time: o.entryTime || null,
          exit_time: o.exitTime || null,
          asset: o.asset,
          side: o.side,
          result_pct: o.resultPct,
          result_money: o.resultMoney ?? null,
          result_r: o.resultR,
          followed_plan: o.followedPlan ?? null,
          notes: o.notes || null,
          tag_ids: o.tagIds,
          created_at: toIso(o.createdAt) ?? new Date().toISOString(),
          updated_at: new Date().toISOString(),
          deleted_at: toIso(o.deletedAt),
          permanent_delete_at: toIso(o.permanentDeleteAt),
          trashed_with_day: o.trashedWithDay ?? null
        }))
      ),
      'operaciones'
    )
  }

  for (const op of operations) await syncOperationImages(userId, op)
}

async function deleteOperationImages(operationId: string): Promise<void> {
  const { data: images } = await supabase
    .from('operation_images')
    .select('id, storage_path')
    .eq('operation_id', operationId)

  if (images && images.length) {
    await supabase.storage.from(SCREENSHOTS_BUCKET).remove(images.map((i: any) => i.storage_path))
    assertOk(
      await supabase
        .from('operation_images')
        .delete()
        .in(
          'id',
          images.map((i: any) => i.id)
        ),
      'capturas (borrado)'
    )
  }
}

async function syncOperationImages(userId: string, op: Operation): Promise<void> {
  const { data: existing } = await supabase
    .from('operation_images')
    .select('id, storage_path')
    .eq('operation_id', op.id)

  const existingIds = new Set((existing ?? []).map((r: any) => r.id))
  const currentIds = new Set(op.images.map((i) => i.id))

  const toDelete = (existing ?? []).filter((r: any) => !currentIds.has(r.id))
  if (toDelete.length) {
    await supabase.storage.from(SCREENSHOTS_BUCKET).remove(toDelete.map((r: any) => r.storage_path))
    assertOk(
      await supabase
        .from('operation_images')
        .delete()
        .in(
          'id',
          toDelete.map((r: any) => r.id)
        ),
      'capturas (borrado)'
    )
  }

  // Screenshots live in a private folder per user: /<userId>/<operationId>/<file>
  for (const image of op.images) {
    if (existingIds.has(image.id)) continue
    if (!image.url.startsWith('data:')) continue
    try {
      const blob = await (await fetch(image.url)).blob()
      const ext = blob.type.split('/')[1] || 'png'
      const path = userId + '/' + op.id + '/' + image.id + '.' + ext
      const { error: uploadError } = await supabase.storage
        .from(SCREENSHOTS_BUCKET)
        .upload(path, blob, { contentType: blob.type, upsert: true })
      assertOk({ error: uploadError }, 'captura (subida)')
      assertOk(
        await supabase.from('operation_images').insert({
          id: image.id,
          operation_id: op.id,
          user_id: userId,
          storage_path: path,
          name: image.name
        }),
        'captura'
      )
    } catch (error) {
      // A failed screenshot must not be reported as saved either.
      throw error instanceof Error ? error : new Error('captura: no se pudo guardar la imagen')
    }
  }
}

async function syncDayJournals(userId: string, dayJournals: Record<string, DayJournal>): Promise<void> {
  const { data: existing } = await supabase.from('day_journals').select('id, date').eq('user_id', userId)
  const existingByDate = new Map((existing ?? []).map((r: any) => [r.date, r.id]))
  const currentDates = new Set(Object.keys(dayJournals))

  const toDelete = (existing ?? []).filter((r: any) => !currentDates.has(r.date))
  if (toDelete.length) {
    assertOk(
      await supabase
        .from('day_journals')
        .delete()
        .in(
          'id',
          toDelete.map((r: any) => r.id)
        ),
      'journals diarios (borrado)'
    )
  }

  for (const [date, journal] of Object.entries(dayJournals)) {
    let dayJournalId = existingByDate.get(date) as string | undefined
    const row = {
      user_id: userId,
      date,
      title: journal.title || null,
      followed_plan: journal.followedPlan ?? null,
      review_status: journal.reviewStatus ?? 'none',
      updated_at: new Date().toISOString(),
      deleted_at: toIso(journal.deletedAt),
      permanent_delete_at: toIso(journal.permanentDeleteAt)
    }

    if (dayJournalId) {
      assertOk(await supabase.from('day_journals').update(row).eq('id', dayJournalId), 'journal diario')
    } else {
      const inserted = await supabase.from('day_journals').insert(row).select('id').single()
      assertOk(inserted, 'journal diario (creación)')
      dayJournalId = inserted.data?.id
    }

    if (dayJournalId) await syncJournalBlocks(userId, dayJournalId, journal.blocks)
  }
}

async function syncJournalBlocks(userId: string, dayJournalId: string, blocks: JournalBlock[]): Promise<void> {
  const { data: existing } = await supabase.from('journal_blocks').select('id').eq('day_journal_id', dayJournalId)
  const existingIds = new Set((existing ?? []).map((r: any) => r.id))
  const currentIds = new Set(blocks.map((b) => b.id))
  const toDelete = [...existingIds].filter((id) => !currentIds.has(id))

  if (toDelete.length) {
    assertOk(await supabase.from('journal_blocks').delete().in('id', toDelete), 'notas (borrado)')
  }

  if (blocks.length) {
    assertOk(
      await supabase.from('journal_blocks').upsert(
        blocks.map((b) => ({
          id: b.id,
          day_journal_id: dayJournalId,
          user_id: userId,
          type: b.type,
          content: b.content ?? null,
          caption: b.caption ?? null,
          checked: b.checked ?? null,
          width: b.width ?? null,
          order_index: b.order
        }))
      ),
      'notas'
    )
  }
}

/** Best-effort local cleanup. Real deletion happens against Supabase (see lib/auth.ts). */
export function dropUserData(userId: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem('dze-journal:v2:' + userId)
    window.localStorage.removeItem('dze-journal:display-mode:' + userId)
  } catch {
    /* non-fatal */
  }
}
